import axios from "axios";
import jwt_decode from "jwt-decode";
import _, { get } from "lodash";
import * as Yup from "yup";

import { bugsnagClient } from "~/shared/utils/errorHandling";
import KickClient from "~/visits/utils/api.js";

import {
  clearConsultAnswersJSON,
  clearOrderItems,
  clearVisitID,
  getBetaBlockerVariant,
  getConsultAnswersJSON,
  getOrderDetailsData,
  getOrderItems,
  getOrderItemsList,
  getOrderItemSubcategoryFromProductSlug,
  getRewardfulAffiliateID,
  getShipmentMethod,
  getVisitAPIToken,
  getVisitDifferentPharmacy,
  getVisitDifferentPharmacyJSON,
  getVisitEmail,
  getVisitID,
  getVisitProduct,
  setBetaBlockerVariant,
  setLinkPersistentTokenCookie,
  setPostCheckoutInfo,
} from "~/visits/utils/visitCookies.js";

import {
  followUpOrderTypeForRefillType,
  getFlavorName,
  isMailOrderBBOrderType,
  isPharmacyPickupFromVisit,
  isPharmacyPickupOrderType,
} from "~/shared/utils/sharedUtils";

import {
  trackBuilderConversion,
  trackPurchaseGTM,
  trackVWOConversion,
} from "~/shared/utils/analytics.js";

import {
  COLOR_KICK_BLUE,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  // Atenolol + Scopolamine Combo
  ORDER_TYPE_ATE_SCO_1_PACK,
  ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
  ORDER_TYPE_ATE_SCO_2_PACK,
  ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_2_PACK_REFILL,
  ORDER_TYPE_PROP_1_PACK,
  // Beta Blocker Flavored Order Types
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_REFILL,
  ORDER_TYPE_PROP_2_PACK,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_REFILL,
  ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  ORDER_TYPE_PROP_VISIT_ONLY,
  ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER,
  ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER,
  ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY,
  // Sleep Membership Order Types
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL,
  ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH,
  // Sleep Trial Order Types
  ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH,
  PRICE_ATE_SCO_1_PACK,
  PRICE_ATE_SCO_1_PACK_FOLLOW_UP,
  PRICE_ATE_SCO_1_PACK_REFILL,
  PRICE_ATE_SCO_2_PACK,
  PRICE_ATE_SCO_2_PACK_FOLLOW_UP,
  PRICE_ATE_SCO_2_PACK_REFILL,
  PRICE_ONLINE_VISIT_FEE_FIRST_VISIT_PA,
  PRICE_ONLINE_VISIT_FEE_FOLLOW_UP_PA,
  PRICE_PROP_1_PACK,
  PRICE_PROP_1_PACK_FLAVORED,
  PRICE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  PRICE_PROP_1_PACK_FLAVORED_REFILL,
  PRICE_PROP_1_PACK_FOLLOW_UP,
  PRICE_PROP_1_PACK_REFILL,
  PRICE_PROP_2_PACK,
  PRICE_PROP_2_PACK_FLAVORED,
  PRICE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  PRICE_PROP_2_PACK_FLAVORED_REFILL,
  PRICE_PROP_2_PACK_FOLLOW_UP,
  PRICE_PROP_2_PACK_REFILL,
  PRICE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  PRICE_PROP_TRIAL_PACK_FLAVORED,
  PRICE_PROP_VISIT_ONLY,
  PRICE_PROP_VISIT_ONLY_FOLLOW_UP,
  PRICE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER,
  // Scopolamine
  PRICE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER,
  PRICE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY,
  // Sleep Membership Prices
  PRICE_SLEEP_MEMBERSHIP_MONTHLY,
  PRICE_SLEEP_MEMBERSHIP_MONTHLY_PER_DAY,
  PRICE_SLEEP_MEMBERSHIP_QUARTERLY,
  PRICE_SLEEP_MEMBERSHIP_QUARTERLY_PER_DAY,
  PRICE_SLEEP_MEMBERSHIP_SEMIANNUAL,
  PRICE_SLEEP_MEMBERSHIP_SEMIANNUAL_PER_DAY,
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY_PER_DAY,
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY_PER_DAY,
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL,
  PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL_PER_DAY,
  PRICE_SLEEP_TRIAL_MAIL_ORDER_ONE_MONTH,
  PRICE_SLEEP_TRIAL_MAIL_ORDER_ONE_MONTH_PER_DAY,
  // Sleep Trial Prices
  PRICE_SLEEP_TRIAL_VISIT_ONLY_ONE_MONTH,
  PRICE_SLEEP_TRIAL_VISIT_ONLY_ONE_MONTH_PER_DAY,
  // Shipping Prices
  SHIPPING_METHOD_TWO_DAY,
  SHIPPING_METHOD_TWO_DAY_PRICE,
} from "~/shared/sharedConstants";

export function getSelectedSleepMeds() {
  const consultAnswers = getConsultAnswersJSON() || {};
  let selectedSleepMeds = [];
  Object.values(consultAnswers).forEach((answer) => {
    if (
      answer?.question?.title ===
      "Are there any sleep medications you are interested in learning more about?"
    ) {
      Object.values(answer).forEach((answer1) => {
        if (answer1?.checked) {
          selectedSleepMeds.push(answer1.title);
        }
      });
    }
  });
  selectedSleepMeds = _.without(
    selectedSleepMeds,
    "No - Let my doctor guide me",
  );
  return selectedSleepMeds;
}

export function getBBProductDetails(checkoutSessionID) {
  let bbDosage = "10mg";
  let bbName = "Propranolol";
  const bbVariantCookie = getBetaBlockerVariant(checkoutSessionID);

  // check to set from cookie
  let bbVariantCookieName;
  let bbVariantCookieDosage;
  if (bbVariantCookie) {
    [bbVariantCookieName, bbVariantCookieDosage] =
      getBetaBlockerVariant(checkoutSessionID).split(" ");
    bbName = bbVariantCookieName;
    bbDosage = bbVariantCookieDosage;
  }

  // check to set from questionnaire (priority over cookie)
  let bbConsultAnswers = { ...getConsultAnswersJSON() } || {};
  for (let key in bbConsultAnswers) {
    // remove version from key
    bbConsultAnswers[key.split("-")[0]] = { ...bbConsultAnswers[key] };
  }

  bbConsultAnswers =
    bbConsultAnswers[
      CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP
    ] ||
    bbConsultAnswers[CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY] ||
    {};

  Object.values(bbConsultAnswers).forEach((answer) => {
    if (
      answer?.question?.title.includes("dosage or medication updates") &&
      answer?.selected === "Yes"
    ) {
      Object.values(answer).forEach((answer1) => {
        if (
          answer1?.childAnswers &&
          answer1.childAnswers["special-requests-radio"]?.selectedValue ===
            "I would like to try a higher 20mg dosage of Propranolol"
        ) {
          bbDosage = "20mg";
        }

        if (
          answer1?.childAnswers &&
          answer1.childAnswers["special-requests-radio"]?.selectedValue ===
            "I would like to try a cardio-selective beta blocker like Atenolol"
        ) {
          bbName = "Atenolol";
          bbDosage = "25mg";
        }
      });
    }
  });

  // sync the cookie with the intake
  if (bbVariantCookieName !== bbName || bbVariantCookieDosage !== bbDosage) {
    setBetaBlockerVariant(`${bbName} ${bbDosage}`, checkoutSessionID);
  }

  return {
    bbName,
    bbDosage,
  };
}

export function hasHistoryOfAsthma() {
  let hasAsthma = false;
  let bbConsultAnswers = { ...getConsultAnswersJSON() } || {};
  for (let key in bbConsultAnswers) {
    // remove version from key
    bbConsultAnswers[key.split("-")[0]] = { ...bbConsultAnswers[key] };
  }

  bbConsultAnswers =
    bbConsultAnswers[
      CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP
    ] ||
    bbConsultAnswers[CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY] ||
    {};

  Object.values(bbConsultAnswers).forEach((answer) => {
    if (
      answer?.question?.title ===
      "Have you ever had any of the following conditions?"
    ) {
      for (let key in answer) {
        const answerValue = answer[key];
        if (answerValue?.title?.includes("asthma") && answerValue?.checked) {
          hasAsthma = true;
        }
      }
    }
  });
  return hasAsthma;
}

export function productName(product, subcategorySlug, checkoutSessionID) {
  const { bbName, bbDosage } = getBBProductDetails(checkoutSessionID);
  const p = product || getVisitProduct(checkoutSessionID);
  subcategorySlug =
    subcategorySlug ||
    getOrderItemSubcategoryFromProductSlug(p, checkoutSessionID);
  const subcategoryName = subcategorySlug ? getFlavorName(subcategorySlug) : "";

  switch (p) {
    // Beta Blocker
    case ORDER_TYPE_PROP_1_PACK:
      return `One Pack ${bbName} ${bbDosage} (24 Tablets)`;
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return `One Pack ${bbName} ${bbDosage} (24 Tablets) Follow-Up Discount`;
    case ORDER_TYPE_PROP_1_PACK_REFILL:
      return `One Pack ${bbName} ${bbDosage} (24 Tablets) Refill`;
    case ORDER_TYPE_PROP_2_PACK:
      return `Two Packs ${bbName} ${bbDosage} (48 Tablets)`;
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return `Two Packs ${bbName} ${bbDosage} (48 Tablets) Follow-Up Discount`;
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return `Two Packs ${bbName} ${bbDosage} (48 Tablets) Refill`;
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Online Doctor Visit Only";

    // Beta Blocker Flavored
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
      return `One Pack ${bbName} ${subcategoryName} (24 Tablets)`;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return `One Pack ${bbName} ${subcategoryName} (24 Tablets) Follow-Up Discount`;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL:
      return `One Pack ${bbName} ${subcategoryName} (24 Tablets) Refill`;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return `Two Packs ${bbName} ${subcategoryName} (48 Tablets)`;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return `Two Packs ${bbName} ${subcategoryName} (48 Tablets) Follow-Up Discount`;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL:
      return `Two Packs ${bbName} ${subcategoryName} (48 Tablets) Refill`;
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return `Sample Pack ${bbName} ${subcategoryName} (6 Tablets)`;
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return `All Flavors Sample Pack ${bbName} (12 Tablets)`;

    // Sleep
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
      return "12-Week Sleep Plan with All Medications Delivered";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
      return "12-Week Sleep Plan with All Medication Delivered";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
      return "12-Week Sleep Plan Local Pharmacy Pickup";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
      return "12-Week Sleep Plan Local Pharmacy Pickup";

    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH:
      return "One Month Sleep Plan - Shipped Free";
    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH:
      return "One Month Sleep Plan (Visit Only)";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
      return "Semi-Annual Sleep Rx Plan - Shipped Free";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
      return "Semi-Annual Sleep Rx Plan (Visit Only)";

    // Scopolamine
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
      return "Add-on: Scopolamine Patch (4 Pack)";
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return "Add-on: Scopolamine Patch (10 Pack)";
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      return "Add-on: Scopolamine Patch (Online Visit Fee)";

    // Atenolol + Scopolamine Combo
    case ORDER_TYPE_ATE_SCO_1_PACK:
      return `One Pack Atenolol + Scopolamine ${subcategoryName} (24 Tablets)`;
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
      return `One Pack Atenolol + Scopolamine ${subcategoryName} (24 Tablets) Refill`;
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return `One Pack Atenolol + Scopolamine ${subcategoryName} (24 Tablets) Follow-Up Discount`;
    case ORDER_TYPE_ATE_SCO_2_PACK:
      return `Two Packs Atenolol + Scopolamine ${subcategoryName} (48 Tablets)`;
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
      return `Two Packs Atenolol + Scopolamine ${subcategoryName} (48 Tablets) Refill`;
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return `Two Packs Atenolol + Scopolamine ${subcategoryName} (48 Tablets) Follow-Up Discount`;
  }

  return "";
}

export function medicationPrice(checkoutSessionID) {
  let medPrice = "N/A";

  switch (getVisitProduct(checkoutSessionID)) {
    // Performance Anxiety
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      medPrice = 20;
      break;
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      medPrice = 40;
      break;
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      medPrice = "Pay At Pharmacy";
      break;
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
      medPrice = 30;
      break;
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      medPrice = 50;
      break;
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      medPrice = "Pay At Pharmacy";
      break;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL:
      medPrice = 35;
      break;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL:
      medPrice = 70;
      break;

    // Atenolol + Scopolamine Combo
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      medPrice = 48;
      break;
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      medPrice = 73;
      break;

    // Sleep
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
      medPrice = 20;
      break;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
      medPrice = 80;
      break;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
      medPrice = 150;
      break;
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH:
      medPrice = 30;
      break;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH:
      medPrice = "Pay At Pharmacy";
      break;
  }

  return typeof medPrice === "number" ? `$${medPrice.toFixed(2)}` : medPrice;
}

function getShippingPrice(checkoutSessionID) {
  switch (getShipmentMethod(checkoutSessionID)) {
    case SHIPPING_METHOD_TWO_DAY:
      return SHIPPING_METHOD_TWO_DAY_PRICE;
    default:
      return 0;
  }
}

export function productTotalPriceWithShipping(checkoutSessionID) {
  return (
    getProductPrice(null, checkoutSessionID) +
    getShippingPrice(checkoutSessionID)
  ).toLocaleString();
}

function getProductPriceWithDollarSign(product, checkoutSessionID) {
  return `$${getProductPrice(product, checkoutSessionID)}`;
}

export function getProductPrice(product, checkoutSessionID) {
  const p = product || getVisitProduct(checkoutSessionID);
  switch (p) {
    case ORDER_TYPE_PROP_1_PACK:
      return PRICE_PROP_1_PACK;
    case ORDER_TYPE_PROP_1_PACK_REFILL:
      return PRICE_PROP_1_PACK_REFILL;
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return PRICE_PROP_1_PACK_FOLLOW_UP;

    case ORDER_TYPE_PROP_2_PACK:
      return PRICE_PROP_2_PACK;
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return PRICE_PROP_2_PACK_REFILL;
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return PRICE_PROP_2_PACK_FOLLOW_UP;

    case ORDER_TYPE_PROP_VISIT_ONLY:
      return PRICE_PROP_VISIT_ONLY;
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return PRICE_PROP_VISIT_ONLY_FOLLOW_UP;

    // Performance Anxiety Flavored
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
      return PRICE_PROP_1_PACK_FLAVORED;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return PRICE_PROP_1_PACK_FLAVORED_FOLLOW_UP;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL:
      return PRICE_PROP_1_PACK_FLAVORED_REFILL;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return PRICE_PROP_2_PACK_FLAVORED;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return PRICE_PROP_2_PACK_FLAVORED_FOLLOW_UP;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL:
      return PRICE_PROP_2_PACK_FLAVORED_REFILL;
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return PRICE_PROP_TRIAL_PACK_FLAVORED;
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return PRICE_PROP_SAMPLE_PACK_ALL_FLAVORS;

    // Sleep Membership Mail Order
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
      return PRICE_SLEEP_MEMBERSHIP_MONTHLY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
      return PRICE_SLEEP_MEMBERSHIP_QUARTERLY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
      return PRICE_SLEEP_MEMBERSHIP_SEMIANNUAL;
    // Sleep Membership Visit Only
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
      return PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
      return PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
      return PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL;
    // Sleep Membership Trials
    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH:
      return PRICE_SLEEP_TRIAL_VISIT_ONLY_ONE_MONTH;
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH:
      return PRICE_SLEEP_TRIAL_MAIL_ORDER_ONE_MONTH;

    // Scopolamine
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
      return PRICE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER;
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return PRICE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER;
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      return PRICE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY;

    // Atenolol + Scopolamine Combo
    case ORDER_TYPE_ATE_SCO_1_PACK:
      return PRICE_ATE_SCO_1_PACK;
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
      return PRICE_ATE_SCO_1_PACK_REFILL;
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return PRICE_ATE_SCO_1_PACK_FOLLOW_UP;
    case ORDER_TYPE_ATE_SCO_2_PACK:
      return PRICE_ATE_SCO_2_PACK;
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
      return PRICE_ATE_SCO_2_PACK_REFILL;
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return PRICE_ATE_SCO_2_PACK_FOLLOW_UP;
  }
}

export function orderTotalPrice(checkoutSessionID) {
  // iterate through order items and sum up the total price
  const orderItems = getOrderItemsList(checkoutSessionID);
  if (orderItems?.length) {
    let total = 0;
    orderItems.forEach(({ productSlug, quantity }) => {
      // Skip if productSlug is null like in case of saying no thanks to scopolamine
      if (productSlug) {
        total += getProductPrice(productSlug, checkoutSessionID) * quantity;
      }
    });
    return total;
  }
  return getProductPrice(null, checkoutSessionID);
}

export function orderTotalPriceWithShipping(checkoutSessionID) {
  return (
    orderTotalPrice(checkoutSessionID) + getShippingPrice(checkoutSessionID)
  );
}

export function orderTotalPriceWithCouponDiscount(
  withShipping,
  checkoutSessionID,
  promoCode = null,
) {
  let price = withShipping
    ? orderTotalPriceWithShipping(checkoutSessionID)
    : orderTotalPrice(checkoutSessionID);

  // Promo code takes precedence over Rewardful coupon
  if (promoCode && promoCode.isValid) {
    if (promoCode.percentOff) {
      return (price * (100 - promoCode.percentOff)) / 100;
    }
    if (promoCode.amountOff) {
      return Math.max(0, price - promoCode.amountOff / 100);
    }
  }

  // Fallback to Rewardful coupon
  if (couponIsValidForOrder()) {
    if (window.Rewardful.coupon.percent_off) {
      return (price * (100 - window.Rewardful.coupon.percent_off)) / 100;
    } else if (window.Rewardful.coupon.amount_off) {
      return price - window.Rewardful.coupon.amount_off / 100.0;
    }
  }
  return price;
}

const NULL_DATE = "0001-01-01T00:00:00Z";
export function couponIsValidForOrder() {
  const apiTokenJWT = getVisitAPIToken();
  const decodedToken = jwt_decode(apiTokenJWT);

  if (window.Rewardful && window.Rewardful.coupon) {
    const hasNoSleepVisit =
      !decodedToken.lastSleepPaymentDate ||
      decodedToken.lastSleepPaymentDate === NULL_DATE;
    const hasNoBBVisit =
      !decodedToken.lastBBPaymentDate ||
      decodedToken.lastBBPaymentDate === NULL_DATE;
    const couponName = window.Rewardful.coupon.name.toLowerCase();

    // Specific type restrictions
    if (couponName.includes("1st sleep")) return hasNoSleepVisit;
    if (couponName.includes("1st bb")) return hasNoBBVisit;

    // Any other Rewardful coupon is valid for first-time users
    return hasNoSleepVisit || hasNoBBVisit;
  }
  return false;
}

export function onlineVisitFee(checkoutSessionID) {
  let fee;
  switch (getVisitProduct(checkoutSessionID)) {
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK:
      fee = PRICE_ONLINE_VISIT_FEE_FIRST_VISIT_PA;
      break;

    case ORDER_TYPE_PROP_VISIT_ONLY:
      fee = PRICE_PROP_VISIT_ONLY;
      break;

    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
      fee = PRICE_ONLINE_VISIT_FEE_FOLLOW_UP_PA;
      break;

    // sleep monthly
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
      fee = PRICE_SLEEP_MEMBERSHIP_MONTHLY;
      break;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
      fee = PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY;
      break;
    // sleep quarterly
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
      fee = PRICE_SLEEP_MEMBERSHIP_QUARTERLY;
      break;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
      fee = PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY;
      break;
    // sleep semiannual (not in use)
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
      fee = PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL;
      break;

    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH:
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH:
      fee = PRICE_SLEEP_TRIAL_VISIT_ONLY_ONE_MONTH;
      break;
  }
  return Number(fee).toFixed(2);
}

export function shouldShowCouponDiscount() {
  if (window.Rewardful && !!window.Rewardful.coupon) {
    const cookieAffiliateID = getRewardfulAffiliateID();
    const couponAffiliateID = get(window.Rewardful, "affiliate.id");
    if (cookieAffiliateID === couponAffiliateID) {
      // Block self referrals
      delete window.Rewardful.coupon;
      return false;
    }
    return couponIsValidForOrder();
  }
  return false;
}

export function showCouponDiscount() {
  if (window.Rewardful && window.Rewardful.coupon) {
    if (window.Rewardful.coupon.percent_off) {
      return `${window.Rewardful.coupon.percent_off}%`;
    }
    if (window.Rewardful.coupon.amount_off) {
      return `-$${(window.Rewardful.coupon.amount_off / 100).toFixed(2)}`;
    }
    return "0";
  }
  return "";
}

// Promo code validation and display functions
export async function validatePromoCode(code) {
  if (!code || code.length < 3) {
    return {
      valid: false,
      message: "Promo code must be at least 3 characters",
    };
  }

  try {
    const client = KickClient.newAuthenticatedClient();
    const response = await client.get(
      `/validate-promo-code?code=${encodeURIComponent(code)}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error validating promo code:", error);
    return { valid: false, message: "Error validating promo code" };
  }
}

export function showPromoDiscount(promoCode) {
  if (promoCode && promoCode.isValid) {
    if (promoCode.percentOff) {
      return `${promoCode.percentOff}%`;
    }
    if (promoCode.amountOff) {
      return `$-${(promoCode.amountOff / 100).toFixed(2)}`;
    }
  }
  return showCouponDiscount();
}

function getCouponName() {
  if (window.Rewardful) {
    return get(window.Rewardful, "coupon.name") || "";
  }

  return "";
}

export function originalAddressReadable(addressObj) {
  if (!_.isEmpty(addressObj)) {
    const { address1, address2, city, usState, zip } = addressObj;
    return `${address1}${address2 ? ` ${address2}` : ""}\n${city}, ${
      usState.abbreviation
    } ${zip}`;
  }
}

export function suggestedAddressReadable(addressObj) {
  const {
    street_number,
    route,
    subpremise,
    locality,
    sublocality_level_1,
    administrative_area_level_1,
    administrative_area_level_3,
    postal_code,
    neighborhood,
  } = addressObj;
  const city =
    locality ||
    sublocality_level_1 ||
    administrative_area_level_3 ||
    neighborhood;

  return `${street_number} ${route}${
    subpremise ? ` ${subpremise}` : ""
  }\n${city}, ${administrative_area_level_1} ${postal_code}`.replace("  ", " ");
}

const cleanAddress = (address) => {
  return address.replace("'", "");
};

export const googleValidateAddress = async (apiKey, originalAddress) => {
  try {
    const res = await axios.post(
      `https://addressvalidation.googleapis.com/v1:validateAddress?key=${apiKey}`,
      {
        address: {
          regionCode: "US",
          locality: originalAddress.city,
          administrativeArea: originalAddress.usState.abbreviation,
          postalCode: originalAddress.zip,
          addressLines: cleanAddress(
            `${originalAddress.address1}${
              originalAddress.address2 ? ` ${originalAddress.address2}` : ""
            }`,
          ),
        },
        enableUspsCass: true,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const {
      result: { verdict, address, uspsData },
    } = res.data;

    // if (res.status !== 200) {
    //   console.log("### Non-200 status [googleValidateAddress] res: ", res);
    //   return false;
    // }
    console.log(
      "### [googleValidateAddress] res: ",
      JSON.stringify(res, null, 2),
    );

    const addressComponentsByType = {};
    address.addressComponents.forEach(({ componentType, componentName }) => {
      addressComponentsByType[componentType] = componentName?.text;
    });

    const hasInferredComponentsOtherThanSuffix = address.addressComponents
      .filter(({ componentType }) => componentType !== "postal_code_suffix")
      .some(({ inferred, spellCorrected }) => inferred || spellCorrected);

    const hasDifferenceWithUSPSState =
      addressComponentsByType.administrative_area_level_1 !==
      uspsData.standardizedAddress.state;

    if (verdict.hasUnconfirmedComponents) {
      const unconfirmedComponents = {};
      address.unconfirmedComponentTypes.forEach((type) => {
        unconfirmedComponents[type] = address.addressComponents?.find(
          ({ componentType }) => componentType === type,
        ).componentName?.text;
      });
      return {
        modalName: "couldntVerifyAddress",
        modalData: {
          originalAddress,
          unconfirmedComponents,
        },
      };
    } else if (
      verdict.hasReplacedComponents ||
      hasInferredComponentsOtherThanSuffix ||
      hasDifferenceWithUSPSState
    ) {
      const suggestedAddress = {};
      address.addressComponents.forEach(
        ({ componentType, componentName: { text }, spellCorrected }) => {
          let addressText = text;
          if (
            componentType === "administrative_area_level_1" &&
            !spellCorrected
          ) {
            addressText = uspsData.standardizedAddress.state;
          }
          suggestedAddress[componentType] = addressText;
        },
      );

      // if the suggested address is the same as the original address, don't show the modal
      if (
        originalAddressReadable(originalAddress).toLowerCase() ===
        suggestedAddressReadable(suggestedAddress).toLowerCase()
      ) {
        return false;
      }

      return {
        modalName: "confirmAddress",
        modalData: {
          originalAddress,
          suggestedAddress,
        },
      };
    } else if (
      address.missingComponentTypes &&
      address.missingComponentTypes.includes("subpremise") &&
      !addressComponentsByType.post_box
    ) {
      return {
        modalName: "missingAddressComponents",
        modalData: {
          originalAddress,
        },
      };
    } else {
      return false;
    }
  } catch (e) {
    console.error("[googleValidateAddress] Error: ", e);
    bugsnagClient.user = {
      email: getVisitEmail(),
    };
    bugsnagClient.notify(e);
    return false;
  }
};

const checkAndSetStripeLinkPersistentToken = (persistentToken) => {
  if (persistentToken && persistentToken != "") {
    setLinkPersistentTokenCookie(persistentToken);
  }
};

export const completeCheckout = async (
  paymentMethodID,
  invoiceID,
  postData,
  setMyCreditCardError,
) => {
  var finalizeData = {
    paymentMethodID: paymentMethodID,
    ...postData,
  };
  console.log(
    "$$$ completeCheckout POST body:",
    JSON.stringify(finalizeData, null, 2),
  );

  const client = KickClient.newAuthenticatedClient();
  try {
    const result = await client.post("/complete-checkout", finalizeData);

    // drop a persistent cookie if we have a link persistent token
    checkAndSetStripeLinkPersistentToken(result.data.linkPersistentToken);

    // Normalize the result to contain the object returned by Stripe.
    // Add the additional details we need.
    return {
      orderType: postData.orderType,
      paymentMethodID: result.data.paymentMethodID,
      priceID: result.data.priceID,
      // invoiceID: result.data.invoiceID,
      result: result,
    };
  } catch (err) {
    bugsnagClient.user = {
      email: getVisitEmail(),
    };
    bugsnagClient.notify(err);

    // console.error("### Error Subscribing:", err.response);
    // console.error("### Error Data:", err.response.data);

    // if (err.response.data && err.response.data.invoiceID) {
    //   setLatestVisitInvoiceID(err.response.data.invoiceID);
    // }
    if (err.response?.data?.error) {
      setMyCreditCardError(err.response.data.error);
    } else {
      setMyCreditCardError(
        "There was an error processing your payment. Please try again.",
      );
    }

    return err.response?.data;
  }
};

export function trackCheckout(checkoutSessionID) {
  const totalPriceNoShipping = orderTotalPriceWithCouponDiscount(
    false,
    checkoutSessionID,
  );
  const totalPriceFloat = parseFloat(totalPriceNoShipping);
  const visitID = getVisitID(checkoutSessionID);

  try {
    // Fire GTM/GA4 purchase event

    trackPurchaseGTM({
      event: "visit_checkout",
      transaction_id: visitID,
      value: totalPriceFloat,
      tax: 0,
      shipping: getShippingPrice(checkoutSessionID),
      currency: "USD",
      coupon: getCouponName(),
    });

    // builder.io tracking
    console.log("tracking conversion - builder:  ", totalPriceNoShipping);
    trackBuilderConversion(totalPriceFloat);
    console.log(
      "builder tracking conversion complete:  ",
      totalPriceNoShipping,
    );

    trackVWOConversion(totalPriceNoShipping);

    // rewardful affiliate conversion tracking
    if (window.rewardful) {
      window.rewardful("convert", { email: getVisitEmail() });
    }
  } catch (error) {
    bugsnagClient.user = {
      email: getVisitEmail(),
    };

    bugsnagClient.notify(error);
    console.error("[trackPurchaseGTM] Error: ", error);
  }
}

export const getStripeOptions = (setupIntentClientSecret) => {
  const appearance = {
    theme: "stripe",
    variables: {
      colorText: "#1e2347",
      colorPrimary: COLOR_KICK_BLUE,
      fontFamily: '"BasisGrotesquePro-Regular", Helvetica, sans-serif',
    },
  };

  return { ...setupIntentClientSecret, appearance };
};

// constructs the json object to post to kick server containing payment and pick-up pharmacy info
export function checkoutPostData(
  values,
  consultationType,
  checkoutSessionID,
  promoCode = null,
) {
  const visitID = getVisitID(checkoutSessionID);
  if (!visitID) {
    console.warn(
      "[checkoutPostData] Missing visitID - likely a duplicate submission",
    );
    return null;
  }
  const orderType = getVisitProduct(checkoutSessionID);
  const orderDetails = getOrderDetailsData(checkoutSessionID);
  let orderItems = getOrderItemsList(checkoutSessionID);

  // make sure refill order types are converted to follow-up order types
  if (orderType.includes("follow-up")) {
    orderItems = orderItems.map((item) => ({
      ...item,
      productSlug: followUpOrderTypeForRefillType(item.productSlug),
    }));
  }

  let postData = {
    ...orderDetails,
    cardName: values.cardName.trim(),
    consultationType: consultationType || "",
    orderItems,
    orderType,
    shipmentMethod: "",
    visitID,
  };

  // pharmacy pickup
  if (isPharmacyPickupOrderType(orderType)) {
    postData.pharmacyInfo = getVisitDifferentPharmacyJSON(checkoutSessionID);
  }
  // shipment method
  if (isMailOrderBBOrderType(orderType)) {
    postData.shipmentMethod = getShipmentMethod(checkoutSessionID);
  }

  // Check for promo code first
  if (promoCode && promoCode.isValid) {
    postData = {
      ...postData,
      promoCodeID: promoCode.promotionCodeID,
      couponID: promoCode.couponID,
      couponName: promoCode.couponName || promoCode.code,
      totalPrice: orderTotalPriceWithCouponDiscount(
        true,
        checkoutSessionID,
        promoCode,
      ),
    };
    // If promo code maps to an affiliate, include attribution data
    if (promoCode.rewardfulAffiliateID) {
      postData.rewardfulAffiliateID = promoCode.rewardfulAffiliateID;
      postData.rewardfulCampaignID = promoCode.rewardfulCampaignID;
    }
  }
  // Fallback: Attach Rewardful details to checkout values if they exist
  else if (
    typeof Rewardful !== "undefined" &&
    window.Rewardful &&
    window.Rewardful.referral
  ) {
    postData = {
      ...postData,
      rewardfulReferralID: get(window.Rewardful, "referral"),
      rewardfulAffiliateID: get(window.Rewardful, "affiliate.id"),
      rewardfulCampaignID: get(window.Rewardful, "campaign.id"),
      couponID: get(window.Rewardful, "coupon.id"),
      couponName: getCouponName(),
      totalPrice: orderTotalPriceWithCouponDiscount(true, checkoutSessionID),
    };
  }

  return postData;
}

export const orderDetailsFormValidationSchema = Yup.object().shape({
  address1: Yup.string()
    .required("Address is required.")
    .max(35, "Address is too long"),
  city: Yup.string().required("City is required.").max(35, "City is too long"),
  usState: Yup.object()
    .nullable()
    .test("state-required", "Please pick a state.", (value) => {
      return value?.abbreviation;
    })
    .required("Please pick a state."),
  zip: Yup.string()
    .required("Zip Code is required.")
    .min(5, "Please enter a Zip Code with at least 5 digits")
    .max(9, "Zip Code is too long"),
  phone: Yup.string().required("Phone number is required."),
});
export const paymentDetailsFormValidationSchema = Yup.object().shape({
  cardName: Yup.string().required("Cardholder name is required."),
});

export const planSavings = (isRecurring, upgradedPlan) => {
  const visitOnly = isPharmacyPickupFromVisit(getVisitDifferentPharmacy());

  let currentPrice = visitOnly
    ? PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY
    : PRICE_SLEEP_MEMBERSHIP_MONTHLY;

  if (!isRecurring) {
    currentPrice = visitOnly
      ? PRICE_SLEEP_TRIAL_VISIT_ONLY_ONE_MONTH
      : PRICE_SLEEP_TRIAL_MAIL_ORDER_ONE_MONTH;
  }

  let upgradedPlanPricePerMonth;
  if (upgradedPlan === "quarterly") {
    upgradedPlanPricePerMonth = visitOnly
      ? PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY
      : PRICE_SLEEP_MEMBERSHIP_QUARTERLY;
    upgradedPlanPricePerMonth = upgradedPlanPricePerMonth / 3;
  } else if (upgradedPlan === "semiannual") {
    upgradedPlanPricePerMonth = visitOnly
      ? PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL
      : PRICE_SLEEP_MEMBERSHIP_SEMIANNUAL;
    upgradedPlanPricePerMonth = upgradedPlanPricePerMonth / 6;
  } else if (upgradedPlan === "monthly") {
    upgradedPlanPricePerMonth = visitOnly
      ? PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY
      : PRICE_SLEEP_MEMBERSHIP_MONTHLY;
  }

  return Math.round(
    ((currentPrice - upgradedPlanPricePerMonth) / currentPrice) * 100,
  );
};

export const isSleepMembership = (checkoutSessionID) => {
  const sleepMemberships = [
    ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
    ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
    ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL,
    ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
    ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
    ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL,
  ];
  return sleepMemberships.includes(getVisitProduct(checkoutSessionID));
};

export const getDailyMembershipCost = (checkoutSessionID) => {
  switch (getVisitProduct(checkoutSessionID)) {
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
      return PRICE_SLEEP_MEMBERSHIP_MONTHLY_PER_DAY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
      return PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY_PER_DAY;
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH:
      return PRICE_SLEEP_TRIAL_MAIL_ORDER_ONE_MONTH_PER_DAY;
    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH:
      return PRICE_SLEEP_TRIAL_VISIT_ONLY_ONE_MONTH_PER_DAY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
      return PRICE_SLEEP_MEMBERSHIP_QUARTERLY_PER_DAY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
      return PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY_PER_DAY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
      return PRICE_SLEEP_MEMBERSHIP_SEMIANNUAL_PER_DAY;
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
      return PRICE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL_PER_DAY;
  }
};

export async function completeCheckoutAndRedirect({
  paymentMethodID,
  postData,
  checkoutSessionID,
  consultationType,
  history,
  setError,
}) {
  if (!postData) {
    console.warn(
      "[completeCheckoutAndRedirect] No postData - skipping duplicate submission",
    );
    return;
  }

  const result = await completeCheckout(
    paymentMethodID,
    "", // invoiceID
    postData,
    setError,
  );

  if (result?.error || result?.ERROR || result?.data?.ERROR) {
    console.error("[completeCheckoutAndRedirect] error: ", result);
    return;
  }

  if (!result || !result.result) {
    setError("There was an error processing your visit. Please try again.");
    return;
  }

  trackCheckout(checkoutSessionID);

  setPostCheckoutInfo(
    {
      visitID: getVisitID(checkoutSessionID),
      orderItems: getOrderItems(checkoutSessionID),
      product: getVisitProduct(checkoutSessionID),
      consultationType,
    },
    checkoutSessionID,
  );

  clearVisitID(checkoutSessionID);
  clearOrderItems(checkoutSessionID);

  // Redirect to confirmation
  let shouldRedirect = true;
  if (result?.result?.data) {
    shouldRedirect = result.result.data.requiresIDVerification;
  }

  let redirectURL = `/visits/${consultationType}/payment/complete?checkoutSessionID=${checkoutSessionID}`;
  if (shouldRedirect) {
    redirectURL += "&requiresIDVerification=true";
  }
  clearConsultAnswersJSON(checkoutSessionID);
  history.push(redirectURL);
}

export function getFollowUpOrOriginalPrice(originalOrderType, isFollowUp) {
  if (isFollowUp) {
    switch (originalOrderType) {
      case ORDER_TYPE_PROP_2_PACK:
        return getProductPriceWithDollarSign(ORDER_TYPE_PROP_2_PACK_FOLLOW_UP);
      case ORDER_TYPE_PROP_1_PACK:
        return getProductPriceWithDollarSign(ORDER_TYPE_PROP_1_PACK_FOLLOW_UP);
      case ORDER_TYPE_PROP_VISIT_ONLY:
        return getProductPriceWithDollarSign(
          ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP,
        );
      case ORDER_TYPE_PROP_1_PACK_FLAVORED:
        return getProductPriceWithDollarSign(
          ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        );
      case ORDER_TYPE_PROP_2_PACK_FLAVORED:
        return getProductPriceWithDollarSign(
          ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
        );
      case ORDER_TYPE_ATE_SCO_1_PACK:
        return getProductPriceWithDollarSign(
          ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
        );
      case ORDER_TYPE_ATE_SCO_2_PACK:
        return getProductPriceWithDollarSign(
          ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
        );
    }
  } else {
    return getProductPriceWithDollarSign(originalOrderType);
  }
}
